import React, { useState, useEffect } from 'react';
import { FaTimes, FaSearch, FaUserPlus, FaEnvelope, FaUsers, FaCheck, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import styles from './TeamInvitationModal.module.css';
import { teamInvitationApi, USER_TYPE } from '../../api/teamInvitations';
import { useUser } from '../../context/UserContext';
import { useNotification } from '../../context/NotificationContext';

const TeamInvitationModal = ({ 
  team, 
  clubId, 
  programId, 
  onClose, 
  onInvitationSent 
}) => {
  const { userProfile } = useUser();
  const { showSuccess, showError, showWarning } = useNotification();
  const [step, setStep] = useState(1); // 1: Email search, 2: Role selection, 3: Confirmation
  const [email, setEmail] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [invitationMessage, setInvitationMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (team?._id) {
      fetchAvailableRoles();
    }
  }, [team]);

  const fetchAvailableRoles = async () => {
    try {
      const response = await teamInvitationApi.getAvailableRoles(team._id);
      if (response.success) {
        setAvailableRoles(response.roles || []);
      }
    } catch (error) {
      // Silently handle role fetching errors
    }
  };

  const handleEmailSearch = async () => {
    if (!email.trim()) {
      showError('Please enter an email address');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      showError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setSearchResults(null);

    try {
      const response = await teamInvitationApi.searchUsersByEmail(email.trim(), clubId, programId);
      
      if (response.success) {
        setSearchResults(response);
        if (response.userType === USER_TYPE.EXISTING_USER) {
          setSelectedUser(response.user);
          setStep(2); // Go directly to role selection for existing users
        } else {
          setStep(2); // Go to role selection for new users
        }
      } else {
        showError(response.message || 'User search failed');
      }
    } catch (error) {
      showError('Error searching for user');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
  };

  const handleSendInvitation = async () => {
    if (!selectedRole) {
      showError('Please select a role for the invitation');
      return;
    }

    // Validate required fields
    if (!email.trim()) {
      showError('Email is required');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      showError('Please enter a valid email address');
      return;
    }

    if (!team?._id) {
      showError('Team information is missing');
      return;
    }

    if (!userProfile?._id) {
      showError('User profile is missing. Please refresh the page.');
      return;
    }

    // Message length validation
    if (invitationMessage && invitationMessage.length > 500) {
      showError('Message cannot exceed 500 characters');
      return;
    }

    setLoading(true);

    try {
      const invitationData = {
        email: email.trim(),
        roleKey: selectedRole.roleKey,
        roleName: selectedRole.roleName,
        message: invitationMessage.trim(),
        clubId: clubId || null,
        programId: programId || null,
        teamName: team.name,
        invitedBy: userProfile._id
      };

      const response = await teamInvitationApi.sendInvitation(team._id, invitationData);
      
      if (response.success) {
        showSuccess('Invitation sent successfully!');
        setTimeout(() => {
          onInvitationSent && onInvitationSent(response.invitation);
          onClose();
        }, 1500);
      } else {
        showError(response.message || 'Failed to send invitation');
      }
    } catch (error) {
      // Handle specific error cases from the API documentation
      if (error.status === 400) {
        // Try to extract detailed validation error from response
        let errorMessage = 'Invalid invitation data';
        if (error.response?.message) {
          errorMessage = error.response.message;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        showError(errorMessage);
      } else if (error.status === 401) {
        showError('Please log in again to send invitations');
      } else if (error.status === 403) {
        showError('You do not have permission to send invitations for this team. Only team coaches, assistant coaches, and program managers can send invitations.');
      } else if (error.status === 404) {
        // Check if user needs to complete registration
        if (error.response?.data?.firebaseUid) {
          showError('User needs to complete their registration first');
        } else {
          showError('Team not found. Please refresh the page and try again.');
        }
      } else if (error.status === 409) {
        showError('There is already a pending invitation for this email');
      } else if (error.status === 500) {
        showError('Server error occurred. Please try again later.');
      } else {
        showError(`Error sending invitation: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const getRoleStatus = (role) => {
    if (role.maxCount && role.currentCount >= role.maxCount) {
      return 'full';
    }
    if (role.minCount && role.currentCount < role.minCount) {
      return 'needs-more';
    }
    return 'available';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'full': return <FaTimes className={styles.statusIcon} />;
      case 'needs-more': return <FaExclamationTriangle className={styles.statusIcon} />;
      case 'available': return <FaCheck className={styles.statusIcon} />;
      default: return null;
    }
  };

  const renderEmailSearchStep = () => (
    <div className={styles.step}>
      <div className={styles.stepHeader}>
        <h3>Search User by Email</h3>
        <p>Enter the email address of the person you want to invite to the team</p>
      </div>

      <div className={styles.emailSearch}>
        <div className={styles.searchInput}>
          <FaEnvelope className={styles.inputIcon} />
          <input
            type="email"
            placeholder="Enter email address..."
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleEmailSearch()}
            disabled={loading}
          />
          <button 
            className={styles.searchButton}
            onClick={handleEmailSearch}
            disabled={loading || !email.trim()}
          >
            <FaSearch />
          </button>
        </div>
      </div>

      {loading && <div className={styles.loading}>Searching for user...</div>}
    </div>
  );

  const renderRoleSelectionStep = () => (
    <div className={styles.step}>
      <div className={styles.stepHeader}>
        <h3>Select Role & Confirm Invitation</h3>
        <p>Choose the role for {searchResults?.userType === USER_TYPE.EXISTING_USER ? selectedUser?.name : email}</p>
      </div>

      {/* User Info */}
      <div className={styles.userInfo}>
        {searchResults?.userType === USER_TYPE.EXISTING_USER ? (
          <div className={styles.existingUser}>
            <FaUserPlus className={styles.userIcon} />
            <div className={styles.userDetails}>
              <h4>{selectedUser?.name}</h4>
              <p>{selectedUser?.email}</p>
              <span className={styles.userType}>Existing User</span>
            </div>
          </div>
        ) : (
          <div className={styles.newUser}>
            <FaEnvelope className={styles.userIcon} />
            <div className={styles.userDetails}>
              <h4>{email}</h4>
              <p>New User - Will receive club & program joining links</p>
              <span className={styles.userType}>New User</span>
            </div>
          </div>
        )}
      </div>

      {/* Role Selection */}
      <div className={styles.roleSelection}>
        <h4>Available Roles</h4>
        <div className={styles.rolesList}>
          {availableRoles.map((role) => {
            const status = getRoleStatus(role);
            const isSelected = selectedRole?.roleKey === role.roleKey;
            
            return (
              <div
                key={role.roleKey}
                className={`${styles.roleItem} ${styles[status]} ${isSelected ? styles.selected : ''}`}
                onClick={() => handleRoleSelect(role)}
              >
                <div className={styles.roleHeader}>
                  <div className={styles.roleName}>
                    <h5>{role.roleName}</h5>
                    {getStatusIcon(status)}
                  </div>
                  <div className={styles.roleCount}>
                    <FaUsers className={styles.countIcon} />
                    {role.currentCount}/{role.maxCount || '∞'}
                  </div>
                </div>
                
                <p className={styles.roleDescription}>{role.description}</p>
                
                <div className={styles.roleDetails}>
                  {role.minCount > 0 && (
                    <span className={styles.minCount}>Min: {role.minCount}</span>
                  )}
                  {role.maxCount && (
                    <span className={styles.maxCount}>Max: {role.maxCount}</span>
                  )}
                  {role.isRequired && (
                    <span className={styles.required}>Required</span>
                  )}
                </div>
                
                {status === 'full' && (
                  <div className={styles.fullMessage}>Role at maximum capacity</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Invitation Message */}
      <div className={styles.messageSection}>
        <label htmlFor="invitationMessage">
          Invitation Message (Optional)
          <span className={styles.charCounter}>
            {invitationMessage.length}/500 characters
          </span>
        </label>
        <textarea
          id="invitationMessage"
          placeholder="Add a personal message to the invitation..."
          value={invitationMessage}
          onChange={(e) => setInvitationMessage(e.target.value)}
          rows={3}
          maxLength={500}
        />
        {invitationMessage.length > 450 && (
          <div className={styles.charWarning}>
            {500 - invitationMessage.length} characters remaining
          </div>
        )}
      </div>

      {/* New User Flow Info */}
      {searchResults?.userType !== USER_TYPE.EXISTING_USER && (
        <div className={styles.newUserInfo}>
          <FaInfoCircle className={styles.infoIcon} />
          <div className={styles.infoContent}>
            <h5>New User Invitation Process</h5>
            <p>This user will receive:</p>
            <ul>
              <li>Club joining invitation link</li>
              <li>Program enrollment invitation link</li>
              <li>Team role assignment upon joining</li>
            </ul>
          </div>
        </div>
      )}

    </div>
  );

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Loading Overlay */}
        {loading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingSpinner}>
              <div className={styles.spinner}></div>
            </div>
            <div className={styles.loadingMessage}>
              <h3>Sending Invitation...</h3>
              <p>Please wait while we process your invitation</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className={styles.header}>
          <h2>Invite Player to {team?.name}</h2>
          <button className={styles.closeButton} onClick={onClose} disabled={loading}>
            <FaTimes />
          </button>
        </div>

        {/* Progress Steps */}
        <div className={styles.progressSteps}>
          <div className={`${styles.stepIndicator} ${step >= 1 ? styles.active : ''}`}>
            <span>1</span>
            <label>Search User</label>
          </div>
          <div className={`${styles.stepIndicator} ${step >= 2 ? styles.active : ''}`}>
            <span>2</span>
            <label>Select Role</label>
          </div>
        </div>

        {/* Step Content */}
        <div className={styles.stepContent}>
          {step === 1 && renderEmailSearchStep()}
          {step === 2 && renderRoleSelectionStep()}
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          <button className={styles.cancelButton} onClick={onClose} disabled={loading}>
            Cancel
          </button>
          
          {step === 1 && (
            <button 
              className={styles.nextButton}
              onClick={handleEmailSearch}
              disabled={loading || !email.trim()}
            >
              {loading ? 'Searching...' : 'Search User'}
            </button>
          )}
          
          {step === 2 && (
            <button 
              className={styles.sendButton}
              onClick={handleSendInvitation}
              disabled={loading || !selectedRole}
            >
              {loading ? 'Sending...' : 'Send Invitation'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamInvitationModal;
